export const SERVER_SYNC_PROJECT_REFRESH_FAILED_TOAST_DURATION_MS = 5000
export const SERVER_SYNC_PROJECT_REFRESH_FAILED_TOAST_COOLDOWN_MS = 30_000

const projectRefreshFailureShownAt = new Map<string, number>()

export function shouldShowServerSyncProjectRefreshFailedToast(directory: string, now = Date.now()) {
  const shownAt = projectRefreshFailureShownAt.get(directory)
  if (shownAt !== undefined && now - shownAt < SERVER_SYNC_PROJECT_REFRESH_FAILED_TOAST_COOLDOWN_MS) return false
  projectRefreshFailureShownAt.set(directory, now)
  return true
}

export function clearServerSyncProjectRefreshFailedToast(directory: string) {
  projectRefreshFailureShownAt.delete(directory)
}

export function serverSyncProjectRefreshFailedMessage() {
  return "Project data could not be refreshed. Check the connection and try again."
}

export function serverSyncProjectRefreshFailedToast(
  project: string,
  translate: (key: string, vars?: Record<string, string>) => string,
) {
  return {
    variant: "error" as const,
    title: translate("toast.project.reloadFailed.title", { project }),
    description: serverSyncProjectRefreshFailedMessage(),
    duration: SERVER_SYNC_PROJECT_REFRESH_FAILED_TOAST_DURATION_MS,
    persistent: false,
  }
}

export function serverSyncSessionsLoadFailedMessage(detail?: string) {
  const base = "Sessions could not be loaded. Check the project connection and try again."
  const reason = detail?.trim()
  return reason ? `${base} (${reason})` : base
}

// Session loads race the auth token on every sign-in.
//
// Tokens live in memory only (context/auth/token.ts), so on login, re-login, or a
// renderer reload the token slot starts empty and is filled asynchronously — on
// desktop via an IPC round-trip to the main-process broker. AuthGuard opens on
// `state().authenticated`, which flips as soon as a SESSION OBJECT exists, not when
// a token is actually in hand. ServerSync therefore mounts and fires GET /session
// for every project in the same tick, withAuthorization drops the
// x-openanalyst-access-token header because the token is still null, and the engine
// rejects all of them at once:
//
//   auth-middleware path=/session hasAuthorization=true hasAccessTokenHeader=false
//   server error=Missing or invalid Authorization header failed
//
// createAuthFetch does try to recover (refresh, then replay), but a refresh cannot
// mint what has not been restored yet, so it returns the 401 unchanged. Nothing
// re-triggered the load, which left every project's thread list permanently empty
// and the timeline stuck on "Loading messages…".
//
// Retrying on a short budget was the first attempt at this and it was the wrong shape:
// it only covers a token that is a few hundred milliseconds late, and the window that
// actually matters is a user signing out and back in, which takes as long as it takes to
// type an email and an OTP — observed at 41 seconds. Waiting that out with backoff would
// mean either a pointlessly long retry loop or giving up before the token lands.
//
// So the real fix lives in two other places, and this budget is now only a backstop for
// the sub-second case:
//   - ServerSync's `paused()` gate holds refreshes back while no token is available, so
//     they are never spent on a request that cannot succeed (context/server-sync.tsx).
//   - `onAuthTokenChange` re-runs them the moment one lands (context/auth/token.ts).
export const SESSION_LOAD_AUTH_RETRY_DELAYS_MS = [300, 1000, 2500] as const

const AUTH_NOT_READY_MESSAGE = /missing or invalid authorization header|authentication required|unauthorized/i

// A token that was sent and refused is a different failure from one that never arrived, and the two
// used to share this regex. "Invalid or expired token" comes back from token validation upstream,
// which means the header WAS present — retrying re-sends the same dead token and the only thing
// that fixes it is signing in again.
const AUTH_EXPIRED_MESSAGE = /invalid or expired token|token (?:is )?expired|session (?:has )?expired/i

function hasKey<K extends string>(source: object, key: K): source is Record<K, unknown> {
  return key in source
}

function numberField(source: unknown, key: string) {
  if (!source || typeof source !== "object" || !hasKey(source, key)) return undefined
  const value = source[key]
  return typeof value === "number" ? value : undefined
}

function stringField(source: object, key: string) {
  if (!hasKey(source, key)) return undefined
  const value = source[key]
  return typeof value === "string" ? value : undefined
}

function errorStatus(error: unknown) {
  if (!error || typeof error !== "object") return undefined
  const direct = numberField(error, "status") ?? numberField(error, "statusCode")
  if (direct !== undefined) return direct
  // hey-api surfaces the transport response on the thrown error for some shapes.
  return numberField(hasKey(error, "response") ? error.response : undefined, "status")
}

function errorMessage(error: unknown) {
  if (typeof error === "string") return error
  if (!error || typeof error !== "object") return ""
  return ["message", "error", "statusText"]
    .map((key) => stringField(error, key))
    .filter((part): part is string => part !== undefined)
    .join(" ")
}

/**
 * True when the failure looks like "the token has not landed yet" rather than a real
 * problem with the project or the server. Deliberately excludes 403: a forbidden
 * response means the token DID arrive and was refused, and retrying that only delays
 * telling the user something they need to act on.
 */
export function isAuthNotReadyError(error: unknown) {
  // Checked first: an expired token is not a "not ready yet" case, however 401 it looks.
  if (isAuthExpiredError(error)) return false
  if (error instanceof Error && error.name === "AuthRequiredError") return true
  if (errorStatus(error) === 401) return true
  return AUTH_NOT_READY_MESSAGE.test(errorMessage(error))
}

/**
 * True when the token was sent and refused as invalid or expired. Retrying cannot help — the
 * user has to sign in again — so this must never be treated as a transient race.
 */
export function isAuthExpiredError(error: unknown) {
  return AUTH_EXPIRED_MESSAGE.test(errorMessage(error))
}

/**
 * Delay before the next attempt, given how many have already been made, or undefined
 * once the budget is spent. `sessionLoadRetryDelayMs(1)` is the wait after attempt 1.
 */
export function sessionLoadRetryDelayMs(attempt: number) {
  if (!Number.isInteger(attempt) || attempt < 1) return undefined
  return SESSION_LOAD_AUTH_RETRY_DELAYS_MS[attempt - 1]
}

/** Whether to try again for this error after `attempt` attempts have been made. */
export function shouldRetrySessionLoad(error: unknown, attempt: number) {
  if (!isAuthNotReadyError(error)) return false
  return sessionLoadRetryDelayMs(attempt) !== undefined
}

/** Short, user-facing reason pulled off a failure, or undefined when unusable. */
export function sessionLoadFailureDetail(error: unknown) {
  const message = errorMessage(error).trim().replace(/\s+/g, " ")
  if (!message) return undefined
  return message.length > 160 ? `${message.slice(0, 159)}…` : message
}
